import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import type { ProviderAdapter, VideoResolution } from '@byokapp/types'
import type { Model } from '@byokapp/types'
import type { AspectRatio } from '@/components/generate/AspectRatioPicker'
import type { GeneratePhase } from '@/components/generate/OutputDisplay'
import type { ActorCharacterForm } from '@/lib/buildActorPrompt'
import { EMPTY_ACTOR_FORM } from '@/lib/buildActorPrompt'
import { useKeysStore } from '@/stores/keys'
import { useGalleryStore } from '@/stores/gallery'
import { usePresetsStore } from '@/stores/presets'
import { getAdapter } from '@/lib/adapters'
import { getProvider } from '@/lib/providers'
import { useProviderModels } from '@/hooks/useProviderModels'

const POLL_INTERVAL_MS = 5_000
const POLL_MAX_ATTEMPTS = 60

// Only entries where the URL slug differs from the internal provider ID.
// Slugs that match their provider ID (replicate, cloudflare-ai, etc.) pass through via fallback.
const PROVIDER_SLUG_MAP: Record<string, string> = {
  google: 'google-gemini',
  studio: 'google-gemini',
}

export type GenerateEndpoint =
  | 'generate-images'
  | 'generate-videos'
  | 'generate-actors'
  | 'generate-image-to-video'

interface GenerateWorkspaceContextValue {
  providerId: string
  providerSlug: string
  endpoint: GenerateEndpoint | null
  providerName: string
  apiKey: string | null
  keysLoading: boolean
  keyFetching: boolean
  hasKey: boolean
  modelsLoading: boolean
  models: Model[]
  modelId: string
  setModelId: (id: string) => void
  selectedModel: Model | undefined
  isVideo: boolean
  prompt: string
  setPrompt: (v: string) => void
  numImages: number
  setNumImages: (v: number) => void
  maxImages: number
  aspectRatio: AspectRatio
  setAspectRatio: (v: AspectRatio) => void
  durationSeconds: number
  setDurationSeconds: (v: number) => void
  durationOptions: number[] | null
  resolution: VideoResolution
  setResolution: (v: VideoResolution) => void
  resolutionOptions: VideoResolution[] | null
  // Actor form state (only used when endpoint === 'generate-actors')
  actorInputMode: 'form' | 'prompt'
  setActorInputMode: (v: 'form' | 'prompt') => void
  actorForm: ActorCharacterForm
  setActorForm: (v: ActorCharacterForm) => void
  actorFreeformPrompt: string
  setActorFreeformPrompt: (v: string) => void
  // Image-to-video state (only used when endpoint === 'generate-image-to-video')
  imageUrl: string | null
  setImageUrl: (v: string | null) => void
  lastFrameImageUrl: string | null
  setLastFrameImageUrl: (v: string | null) => void
  phase: GeneratePhase
  outputUrls: string[]
  error: string
  busy: boolean
  handleGenerate: () => Promise<void>
}

const GenerateWorkspaceContext = createContext<GenerateWorkspaceContextValue | null>(null)

export function useGenerateWorkspace(): GenerateWorkspaceContextValue {
  const ctx = useContext(GenerateWorkspaceContext)
  if (!ctx) throw new Error('useGenerateWorkspace must be used within GenerateWorkspaceProvider')
  return ctx
}

export function GenerateWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { provider } = useParams()
  const { pathname, state: locationState } = useLocation()

  const providerSlug = provider ?? ''
  const providerId = PROVIDER_SLUG_MAP[providerSlug] ?? providerSlug
  const routeModelType: 'image' | 'video' =
    pathname.endsWith('generate-videos') || pathname.endsWith('generate-image-to-video')
      ? 'video'
      : 'image'
  const isVideo = routeModelType === 'video'

  const endpoint: GenerateEndpoint | null = pathname.endsWith('generate-images')
    ? 'generate-images'
    : pathname.endsWith('generate-videos')
      ? 'generate-videos'
      : pathname.endsWith('generate-actors')
        ? 'generate-actors'
        : pathname.endsWith('generate-image-to-video')
          ? 'generate-image-to-video'
          : null

  // Read preset synchronously on mount if navigated with a presetId in location state.
  // key={pathname} in AppLayout guarantees this provider remounts fresh on navigation,
  // so initialPreset is read exactly once and used as initial state below.
  const presetId = (locationState as { presetId?: string } | null)?.presetId
  const initialPreset = presetId
    ? usePresetsStore.getState().presets.find((p) => p.id === presetId)
    : undefined

  const keysLoading = useKeysStore((s) => s.loading)
  const getKey = useKeysStore((s) => s.getKey)
  const keys = useKeysStore((s) => s.keys)
  const hasKey = keys.some((k) => k.provider === providerId)

  const [apiKey, setApiKey] = useState<string | null>(null)
  // Derived: true when a key is known to exist but hasn't been decrypted yet.
  const keyFetching = !keysLoading && hasKey && apiKey === null
  const { models: allModels, loading: modelsLoading } = useProviderModels(providerId, apiKey)
  const models = allModels.filter((m) => m.type === routeModelType)
  const [modelId, setModelId] = useState(initialPreset?.modelId ?? '')
  const [prompt, setPrompt] = useState(initialPreset?.compiledPrompt ?? '')
  const [numImages, setNumImages] = useState(
    initialPreset?.endpoint === 'generate-images' ? initialPreset.numImages : 1,
  )
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    (initialPreset?.aspectRatio as AspectRatio | undefined) ?? (isVideo ? '16:9' : '1:1'),
  )
  const [durationSeconds, setDurationSeconds] = useState(
    initialPreset?.endpoint === 'generate-videos' ? initialPreset.durationSeconds : 8,
  )
  const [resolution, setResolution] = useState<VideoResolution>(
    initialPreset?.endpoint === 'generate-videos'
      ? (initialPreset.resolution as VideoResolution)
      : '720p',
  )

  // Actor form state — lifted here so GenerateOutputPanel can snapshot it for saving presets.
  const [actorInputMode, setActorInputMode] = useState<'form' | 'prompt'>(
    initialPreset?.endpoint === 'generate-actors' ? initialPreset.inputMode : 'form',
  )
  const [actorForm, setActorForm] = useState<ActorCharacterForm>(
    initialPreset?.endpoint === 'generate-actors' && initialPreset.actorFields
      ? initialPreset.actorFields
      : EMPTY_ACTOR_FORM,
  )
  const [actorFreeformPrompt, setActorFreeformPrompt] = useState(
    initialPreset?.endpoint === 'generate-actors' && initialPreset.inputMode === 'prompt'
      ? initialPreset.compiledPrompt
      : '',
  )

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [lastFrameImageUrl, setLastFrameImageUrl] = useState<string | null>(null)

  const [phase, setPhase] = useState<GeneratePhase>('idle')
  const [outputUrls, setOutputUrls] = useState<string[]>([])
  const [error, setError] = useState('')

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (keysLoading) return
    const cached = useKeysStore.getState()._decryptedCache[providerId]
    if (cached !== undefined) {
      setApiKey(cached)
      return
    }
    setApiKey(null)
    let cancelled = false
    getKey(providerId).then((key) => {
      if (!cancelled) setApiKey(key)
    })
    return () => {
      cancelled = true
    }
  }, [keysLoading, getKey, providerId])

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  useEffect(() => {
    if (models.length > 0) {
      setModelId((currentId) =>
        models.some((m) => m.id === currentId) ? currentId : (models[0]?.id ?? ''),
      )
    }
  }, [models])

  // Reset duration and resolution to valid defaults whenever the selected model changes.
  useEffect(() => {
    const model = models.find((m) => m.id === modelId)
    const durationOpts = model?.capabilities?.durationOptions
    if (durationOpts !== undefined && durationOpts.length > 0) {
      setDurationSeconds((current) =>
        durationOpts.includes(current) ? current : (durationOpts[durationOpts.length - 1] ?? 8),
      )
    }
    const resOpts = model?.capabilities?.resolutionOptions
    if (resOpts !== undefined) {
      setResolution((current) => (resOpts.includes(current) ? current : '720p'))
    }
  }, [modelId, models])

  const selectedModel = models.find((m) => m.id === modelId)
  const durationOptions = selectedModel?.capabilities?.durationOptions ?? null
  const resolutionOptions = selectedModel?.capabilities?.resolutionOptions ?? null
  const maxImages = selectedModel?.capabilities?.maxImages ?? 1
  const busy = phase === 'generating' || phase === 'polling'

  useEffect(() => {
    if (maxImages === 1) setNumImages(1)
  }, [maxImages])

  const nodeLabel = endpoint === 'generate-actors' ? 'AI Actors' : 'Generate'

  function poll(jobId: string, key: string, attempt: number, adapter: ProviderAdapter) {
    pollRef.current = setTimeout(async () => {
      try {
        const result = await adapter.checkStatus(jobId, key)
        if (result.status === 'running' || result.status === 'pending') {
          if (attempt >= POLL_MAX_ATTEMPTS) {
            setPhase('error')
            setError('Generation timed out after 5 minutes.')
            return
          }
          poll(jobId, key, attempt + 1, adapter)
        } else if (result.status === 'succeeded') {
          const urls = await Promise.all(
            result.outputUrls.map(async (uri) => {
              try {
                // WHY: Veo returns a URI requiring key-authenticated GET to fetch bytes.
                // Other providers return direct URLs. Move into adapter.checkStatus()
                // when ProviderAdapter interface gains an output-fetch step.
                if (providerId === 'google-gemini') {
                  const res = await fetch(uri, { headers: { 'x-goog-api-key': key } })
                  const blob = await res.blob()
                  return URL.createObjectURL(blob)
                }
                return uri
              } catch {
                return uri
              }
            }),
          )
          setOutputUrls(urls)
          setPhase('done')
          if (urls[0]) {
            useGalleryStore
              .getState()
              .addItem({ imageUrl: urls[0], nodeLabel, providerId, model: modelId })
          }
        } else if (result.status === 'failed') {
          setPhase('error')
          setError(result.error)
        } else {
          setPhase('error')
          setError('Generation failed.')
        }
      } catch (err) {
        setPhase('error')
        setError(err instanceof Error ? err.message : 'An unexpected error occurred.')
      }
    }, POLL_INTERVAL_MS)
  }

  async function handleGenerate() {
    const adapter = getAdapter(providerId)
    if (!adapter || !apiKey || !modelId || !prompt.trim() || busy) return
    if (pollRef.current) clearTimeout(pollRef.current)

    setPhase('generating')
    setOutputUrls([])
    setError('')

    try {
      if (isVideo) {
        const job = await adapter.generateVideo(
          {
            prompt: prompt.trim(),
            model: modelId,
            aspectRatio: aspectRatio as '16:9' | '9:16',
            ...(durationOptions !== null && { durationSeconds }),
            ...(resolutionOptions !== null && { resolution }),
            ...(imageUrl ? { imageUrl } : {}),
            ...(lastFrameImageUrl ? { lastFrameImageUrl } : {}),
          },
          apiKey,
        )
        setPhase('polling')
        poll(job.jobId, apiKey, 0, adapter)
      } else {
        const job = await adapter.generateImage(
          {
            prompt: prompt.trim(),
            model: modelId,
            aspectRatio,
            ...(maxImages > 1 ? { numImages } : {}),
          },
          apiKey,
        )
        const result = await adapter.checkStatus(job.jobId, apiKey)
        if (result.status === 'succeeded') {
          setOutputUrls(result.outputUrls)
          setPhase('done')
          if (result.outputUrls[0]) {
            useGalleryStore
              .getState()
              .addItem({ imageUrl: result.outputUrls[0], nodeLabel, providerId, model: modelId })
          }
        } else if (result.status === 'failed') {
          setPhase('error')
          setError(result.error)
        } else {
          setPhase('error')
          setError('Generation failed.')
        }
      }
    } catch (err) {
      setPhase('error')
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.')
    }
  }

  const providerName = getProvider(providerId)?.name ?? providerId

  return (
    <GenerateWorkspaceContext.Provider
      value={{
        providerId,
        providerSlug,
        endpoint,
        providerName,
        apiKey,
        keysLoading,
        keyFetching,
        hasKey,
        modelsLoading,
        models,
        modelId,
        setModelId,
        selectedModel,
        isVideo,
        prompt,
        setPrompt,
        numImages,
        setNumImages,
        maxImages,
        aspectRatio,
        setAspectRatio,
        durationSeconds,
        setDurationSeconds,
        durationOptions,
        resolution,
        setResolution,
        resolutionOptions,
        actorInputMode,
        setActorInputMode,
        actorForm,
        setActorForm,
        actorFreeformPrompt,
        setActorFreeformPrompt,
        imageUrl,
        setImageUrl,
        lastFrameImageUrl,
        setLastFrameImageUrl,
        phase,
        outputUrls,
        error,
        busy,
        handleGenerate,
      }}
    >
      {children}
    </GenerateWorkspaceContext.Provider>
  )
}
